import type { PublicationCategory } from '../../../shared/types/common';

/**
 * Подсчёт «Средних просмотров» по аккаунту и по типам публикаций.
 *
 * Тип публикации: категория, поставленная вручную при публикации через
 * InstaFarm, главнее; иначе ролик из «Монтажа» направления B2G — B2G; иначе
 * «без категории». Вручную тип почти не ставят, поэтому без B2G из «Монтажа»
 * разбивка была пустой.
 */

export type CategoryKey = PublicationCategory | 'unknown';

export type ViewsItem = { views: number; category: CategoryKey };

export type ViewStats = {
  count: number;
  mean: number;
  median: number;
  total: number;
  min: number;
  max: number;
};

export function calcStats(views: number[]): ViewStats | null {
  if (views.length === 0) return null;
  const sorted = [...views].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  const total = views.reduce((s, v) => s + v, 0);
  return {
    count: views.length,
    mean: Math.round(total / views.length),
    median: Math.round(median),
    total,
    min: sorted[0],
    max: sorted[sorted.length - 1],
  };
}

export function categoryOf(
  mediaId: string,
  isB2g: boolean,
  manual: Map<string, PublicationCategory>,
): CategoryKey {
  return manual.get(mediaId) ?? (isB2g ? 'b2g' : 'unknown');
}

export type AccountViewsResult = {
  accountId: string;
  platform: 'instagram' | 'tiktok';
  username: string;
  name: string;
  reelsCount: number;
  averageMean: number;
  averageMedian: number;
  totalViews: number;
  minViews: number;
  maxViews: number;
  allViews: number[];
  byCategory: Partial<Record<CategoryKey, number[]>>;
};

export function buildAccountResult(
  account: { accountId: string; platform: 'instagram' | 'tiktok'; username: string; name: string },
  items: ViewsItem[],
): AccountViewsResult | null {
  const views = items.map((i) => i.views);
  const stats = calcStats(views);
  if (!stats) return null;
  const byCategory: Partial<Record<CategoryKey, number[]>> = {};
  for (const item of items) (byCategory[item.category] ??= []).push(item.views);
  return {
    ...account,
    reelsCount: stats.count,
    averageMean: stats.mean,
    averageMedian: stats.median,
    totalViews: stats.total,
    minViews: stats.min,
    maxViews: stats.max,
    allViews: views,
    byCategory,
  };
}

/** Сводка по всем пабликам: средняя и медиана для каждого типа публикаций. */
export function categorySummary(
  results: Pick<AccountViewsResult, 'byCategory'>[],
): Array<{ category: CategoryKey } & ViewStats> {
  const pooled = new Map<CategoryKey, number[]>();
  for (const r of results) {
    for (const [cat, views] of Object.entries(r.byCategory ?? {})) {
      const key = cat as CategoryKey;
      pooled.set(key, [...(pooled.get(key) ?? []), ...(views ?? [])]);
    }
  }
  return [...pooled.entries()]
    .map(([category, views]) => ({ category, ...calcStats(views)! }))
    .filter((s) => s.count > 0)
    .sort((a, b) => (a.category === 'unknown' ? 1 : b.category === 'unknown' ? -1 : b.count - a.count));
}
